import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'urql';

import {
  Box,
  mergeQueryParameters,
  useIntersectionObserver,
  usePushQueryChange,
  useUrl,
} from '@packages/shared';
import { ProductCard } from '@packages/modules';
import { useIsBot } from '@packages/tracking';
import type { ProductCardSpecificProps } from '@packages/modules/src/ProductCard/types';
import { rememberPageData } from '@packages/shared/src/hooks/useScrollRestoration';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { SearchProductCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardFragmentFragmentDoc';
import { SearchProductCardResultFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchProductCardResultFragmentFragmentDoc';
import { SearchProductCardVariationDataDocument } from '@packages/gql/generated/shopping/SearchProductCardVariationDataDocument';
import { useAbTesting } from '@packages/abtesting';

import { useAtom } from 'jotai';
import { getHeightHeuristic } from './heightHeuristic';
import { useProductImpressionTracking } from './tracking/useProductImpressionTracking';
import { useProductClickTracking } from './tracking/useProductClickTracking';
import { buildGlycerinTrackingData } from './tracking/buildGlycerinTrackingData';
import { useVariationDataForProductCard } from './useVariationDataForProductCard';
import { useDisplayMode } from '../../hooks/useDisplayMode';
import { isColorSelectFailedNotificationOpenAtom } from '../ColorSelectFailedNotification/ColorSelectFailedNotification.atoms';
import { encodePage } from '../../queryEncoding';
import { INSTALLMENTS_PAYMENT_TEST_ID } from './useHasInstallmentsPayment';

/* GraphQL */ `
  fragment SearchProductCardResultFragment on SearchProductResult {
    id
    ...SearchProductCardImpressionTrackingResultFragment
    ...SearchProductCardClickTrackingResultFragment
    ...SearchProductCardGlycerinTrackingResultFragment
    ...UseDisplayModeFragment
    abTesting {
      newTestAssignments {
        testId
      }
    }
  }
`;

/* GraphQL */ `
  query SearchProductCardVariationData($resultId: ID!, $styleId: ID!) {
    styleDetails(resultId: $resultId, styleId: $styleId) {
      variationGroups {
        ...SearchProductCardFullVariationGroupFragment
        ...SearchProductCardClickTrackingVariationGroupFragment
        ...SearchProductCardGlycerinTrackingVariationGroupFragment
      }
    }
  }
`;

export type ActualSearchProductCardProps = {
  maskedItem: FragmentType<typeof SearchProductCardFragmentFragmentDoc>;
  maskedResult: FragmentType<typeof SearchProductCardResultFragmentFragmentDoc>;
  pageNumber?: number;
} & Pick<
  ProductCardSpecificProps,
  'priority' | 'useAdditionalFlagsSpacer' | 'useRatingsSpacer' | 'isSponsored'
> & {
    wishListTrackingProps?: Omit<ProductCardSpecificProps['wishListTrackingProps'], 'product'>;
  } & Pick<ProductCardSpecificProps['price'], 'useUnitPriceSpacer'>;

/**
 * A search-page-specific wrapper around `ProductCard`
 */
const ActualSearchProductCard: FC<ActualSearchProductCardProps> = (props) => {
  // local state to allow changing colors, also used for fetching full product data
  const [activeVariationIndex, setActiveVariationIndex] = useState(0);
  // ref to check if data has already failed to fetch
  // because if there is an error the useQuery won't fetch data again
  // unless the query is reexecuted with { requestPolicy: 'network-only' }
  const isErrorAlreadyOccured = useRef<boolean>(false);

  const [isColorSelectFailedNotificationOpen, setIsColorSelectFailedNotificationOpen] = useAtom(
    isColorSelectFailedNotificationOpenAtom,
  );

  const {
    maskedItem,
    maskedResult,
    pageNumber,
    useUnitPriceSpacer,
    wishListTrackingProps,
    ...passthroughProps
  } = props;

  const result = getFragmentData(SearchProductCardResultFragmentFragmentDoc, maskedResult);
  const item = getFragmentData(SearchProductCardFragmentFragmentDoc, maskedItem);

  const [{ data, error, fetching }, executeQuery] = useQuery({
    query: SearchProductCardVariationDataDocument,
    variables: {
      resultId: result.id,
      styleId: item.styleId,
    },
    pause: activeVariationIndex === 0,
    context: useMemo(() => ({ suspense: false }), []),
  });

  // if full variation data is not yet available, fall back to the first variation until data has loaded
  const maskedActiveVariation =
    data?.styleDetails.variationGroups[activeVariationIndex] ?? item.primaryVariationGroup;

  const { dispatchProductClickTracking } = useProductClickTracking(
    result,
    item,
    maskedActiveVariation,
  );

  const { clickTrackingProps, wishListTrackingProduct } = buildGlycerinTrackingData(
    result,
    item,
    maskedActiveVariation,
  );

  const { displayMode } = useDisplayMode(result);
  const { setRelevantProduct } = useAbTesting();

  const activeVariationProps = useVariationDataForProductCard(
    maskedItem,
    activeVariationIndex,
    maskedActiveVariation,
    displayMode,
    pageNumber,
    useUnitPriceSpacer,
  );

  // NOTE: for some reason this does not update with `history.replaceState` correctly, but the page is overwritten below anyways
  const url = useUrl(true);
  const { pushQueryChange } = usePushQueryChange({ trailingSlash: true });

  const handleClick = () => {
    dispatchProductClickTracking();

    const queryChange = encodePage(pageNumber);
    pushQueryChange(queryChange, { shallow: true });

    const modifiedUrl = mergeQueryParameters(url, queryChange);
    rememberPageData(item.styleId, modifiedUrl);

    setRelevantProduct('2024_04_SoFiOn', item.styleId);

    if (activeVariationProps.price.minInstallment) {
      setRelevantProduct(INSTALLMENTS_PAYMENT_TEST_ID, item.styleId);
    }

    // later steps in the customer flow need to be able to correlate the product with the test (e.g. in detail view or checkout)
    for (const testAssignment of result.abTesting?.newTestAssignments ?? []) {
      setRelevantProduct(testAssignment.testId, item.styleId);
    }
  };

  // if failed to fetch full variation data,
  // fall back to the first variation and show a error notification
  useEffect(() => {
    // if there is no error, the active variation is already 0,
    // or query is still fetching, do early return
    if (!error || activeVariationIndex === 0 || fetching) return;

    // if error is already occured in a prior fetch, re-execute the query without using cache
    // and set the error flag to default value
    if (isErrorAlreadyOccured.current && !fetching) {
      executeQuery({ requestPolicy: 'network-only' });
      isErrorAlreadyOccured.current = false;
      return;
    }

    // if error is not already occured in a prior fetch, set the error flag to true,
    // reset active variation index to 0 and show the error notification
    isErrorAlreadyOccured.current = true;
    setActiveVariationIndex(0);
    if (!isColorSelectFailedNotificationOpen) setIsColorSelectFailedNotificationOpen(true);
  }, [
    error,
    activeVariationIndex,
    fetching,
    isColorSelectFailedNotificationOpen,
    setIsColorSelectFailedNotificationOpen,
    executeQuery,
  ]);

  return (
    <ProductCard
      {...passthroughProps}
      {...activeVariationProps}
      clickTrackingProps={clickTrackingProps}
      wishListTrackingProps={{
        ...wishListTrackingProps,
        product: wishListTrackingProduct,
      }}
      shrinkToFit
      onColorChange={setActiveVariationIndex}
      onProductLinkActionAreaClick={handleClick}
    />
  );
};

export type SearchProductCardProps = ActualSearchProductCardProps & {
  alwaysRender?: boolean;
  onFirstBecameVisible?: () => void;
};

/**
 * Specialized version of `ProductCard` for the search product grids
 */
export const SearchProductCard: FC<SearchProductCardProps> = (props) => {
  const { alwaysRender, onFirstBecameVisible, ...actualSearchProductCardProps } = props;

  const { maskedItem, maskedResult } = actualSearchProductCardProps;

  const item = getFragmentData(SearchProductCardFragmentFragmentDoc, maskedItem);
  const result = getFragmentData(SearchProductCardResultFragmentFragmentDoc, maskedResult);

  const { dispatchProductImpressionTracking } = useProductImpressionTracking(result, item);

  const wrapper = useRef<HTMLDivElement>(null);

  // check if the card is fully visible
  useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 1,
    callback: (isIntersecting) => {
      if (!isIntersecting) return;
      // intersectOnce is set to true, so this callback will only be called once
      onFirstBecameVisible?.();
      dispatchProductImpressionTracking();
    },
  });

  // check if the card will scroll into view soon (as defined by the margin)
  const { isIntersecting: shouldRender } = useIntersectionObserver(wrapper, {
    intersectOnce: true,
    threshold: 0,
    rootMargin: '500px 0px 500px 0px',
  });

  const isBot = useIsBot();

  const showActualCard = alwaysRender || shouldRender || isBot;

  const { styleId, styleName } = item;

  const { displayMode } = useDisplayMode(result);

  return (
    <Box
      ref={wrapper}
      sx={{ height: showActualCard ? '100%' : getHeightHeuristic(props, displayMode) }}
      id={styleId}
    >
      {showActualCard ? <ActualSearchProductCard {...actualSearchProductCardProps} /> : styleName}
    </Box>
  );
};
